import { q } from './format.ts';

/** What failed, said from the person's side: the API's codes mapped to Spanish (F4). */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Field-by-field problems (the answers to a questionnaire), in Spanish. */
    readonly errors: { path: string; message: string }[] = [],
  ) {
    super(message);
  }
}
/** A field the person must fix before anything is sent (`field`: its id, to mark it there). */
export class InputError extends Error {
  constructor(
    message?: string,
    readonly field?: string,
  ) {
    super(message);
  }
}

/** Carrier-rule errors, said from the user's side (the parser's own message names the cell). */
const RULE_ERRORS: Record<string, (message: string) => string> = {
  RULESET_TESTS_FAILING: () =>
    'No se puede publicar: hay casos de prueba de la hoja que las reglas no reproducen. Revisa la hoja con la aseguradora o rechaza la propuesta.',
  TARIFF_CELL_INVALID: (m) => `La hoja tiene un dato que Nubi no entiende. ${m}`,
  TARIFF_HEADER_MISSING: (m) => `A la hoja le falta una parte de la plantilla. ${m}`,
  TARIFF_UNKNOWN_LAYOUT: () =>
    'Esta hoja no sigue la plantilla de tarifas y no se pudo reconocer qué hoja es cada tabla.',
  TARIFF_FILE_UNREADABLE: () => 'El archivo no es una hoja de cálculo que se pueda leer.',
  TARIFF_FILE_TOO_LARGE: () => 'La hoja pesa más de 2 MB.',
  // The API already says these from the user's side (limits of the import, in Spanish).
  TARIFF_FILE_NOT_XLSX: (m) => m,
  TARIFF_FILE_UNCOMPRESSED_TOO_LARGE: (m) => m,
  TARIFF_FILE_TOO_COMPLEX: (m) => m,
  TARIFF_TOO_MANY_SHEETS: (m) => m,
  TARIFF_SHEET_TOO_LARGE: (m) => m,
};

/** Reminder texts and the policyholder's consent, said from the user's side. */
const REMINDER_ERRORS: Record<string, (message: string) => string> = {
  UNKNOWN_PLACEHOLDER: (m) => {
    const bad = /cannot use (.+?);/.exec(m)?.[1];
    return `El texto usa ${bad ? `${bad}, un dato` : 'un dato'} que este recordatorio no tiene. Usa solo los datos de la lista.`;
  },
  REMINDER_TEXT_EMPTY: () => 'Escribe el título y el texto del recordatorio.',
  REMINDER_TEXT_TOO_LONG: () => 'El título admite hasta 120 caracteres y el texto hasta 600.',
  POLICYHOLDER_WITHOUT_PHONE: () =>
    'El contratante no tiene teléfono registrado; WhatsApp y SMS necesitan uno.',
  POLICYHOLDER_UNKNOWN: () =>
    'Esta póliza se emitió antes de que Nubi guardara los datos del contratante.',
};

/** A statement entered by hand, said from the user's side (the row or line comes in the message). */
const STATEMENT_ERRORS: Record<string, (message: string) => string> = {
  INVALID_STATEMENT_CSV: (m) =>
    `El CSV tiene una fila que Nubi no entiende${/^Row (\d+)/.exec(m) ? ` (fila ${/^Row (\d+)/.exec(m)?.[1]})` : ''}. Revisa las columnas poliza, vigencia, recibo, tipo, monto y moneda.`,
  INVALID_STATEMENT_LINE: (m) =>
    `Revisa la línea ${/^Line (\d+)/.exec(m)?.[1] ?? ''}: una comisión lleva su número de recibo y un monto mayor que cero.`,
  SAME_PERSON_CANNOT_APPROVE: () =>
    'Quien lo propuso o registró no lo aprueba: lo aprueba otra persona de la dirección.',
  NO_STATEMENT_PROPOSED: () => 'No hay una versión esperando aprobación.',
  NO_APPROVED_STATEMENT: () =>
    'Esa aseguradora no tiene estado de cuenta aprobado de ese mes. Regístralo con «Registrar estado de cuenta» y que la dirección lo apruebe.',
  STATEMENT_INTEGRATED: () =>
    'Esa aseguradora envía su estado de cuenta por sistema; no se registra a mano.',
};

/** Downloadable reports (Excel and PDF). */
const REPORT_ERRORS: Record<string, (message: string) => string> = {
  REPORT_TOO_LARGE: () =>
    'El reporte pasa de 5,000 filas. Elige menos meses o filtra por aseguradora, agente o ramo.',
  INVALID_RANGE: () =>
    'Revisa los meses: «Desde» va antes de «Hasta» y el reporte abarca hasta 24 meses.',
};

/** The agent's invoice (FEL) of a settlement: the API says the differences in Spanish. */
const INVOICE_ERRORS: Record<string, (message: string) => string> = {
  INVOICE_MISMATCH: (m) => `La factura no coincide con la liquidación. ${m}`,
  INVOICE_UNREADABLE: (m) => m,
  INVOICE_PDF_INVALID: (m) => m,
  AGENT_NIT_MISSING: (m) => m,
  AGENCY_NIT_MISSING: (m) => m,
  INVOICE_ALREADY_ACCEPTED: () => 'Esta liquidación ya tiene su factura aceptada.',
  SETTLEMENT_NOT_APPROVED: () => 'La liquidación aún no está aprobada por la dirección.',
  NOTHING_TO_INVOICE: () => 'Esta liquidación no tiene nada que facturar.',
  INVOICE_REQUIRED: () =>
    'Falta la factura del agente: la transferencia se registra cuando su factura coincide.',
};

/** The agency's onboarding form (boleta): the API says these in Spanish already. */
const FORM_ERRORS: Record<string, (message: string) => string> = {
  APPLICATION_FORM_INVALID: (m) => `La boleta no se guardó. ${m}`,
  APPLICATION_INVALID: () => 'Revisa los datos marcados en la boleta.',
  FORM_CHANGED: () =>
    'Alguien más cambió la boleta mientras la editabas. Actualiza la pantalla y vuelve a hacer tus cambios.',
};

/** Commission advances, said to the agent or the director. */
const ADVANCE_ERRORS: Record<string, (message: string) => string> = {
  ADVANCE_EXCEEDS_LIMIT: (m) => {
    const limit = /Q(\d+)\.(\d{2})/.exec(m);
    return `Ese monto pasa de lo que puedes pedir hoy${limit ? ` (${q(Number(limit[1]) * 100 + Number(limit[2]))})` : ''}. Pide un monto menor.`;
  },
  ADVANCES_NOT_ALLOWED: () =>
    'Tu agencia no tiene anticipos habilitados; la dirección los habilita en Liquidaciones.',
  ADVANCE_AMOUNT_INVALID: () => 'El anticipo es un monto en quetzales mayor que cero.',
  REASON_REQUIRED: () => 'Escribe el motivo del rechazo; la agente lo verá.',
  ADVANCE_NOT_REQUESTED: () => 'Ese anticipo ya se decidió. Actualiza la pantalla.',
  ADVANCE_NOT_APPROVED: () => 'Solo se registra el pago de un anticipo aprobado.',
};

/** Issuing: who sells the policy (and earns its commission). */
const ISSUE_ERRORS: Record<string, (message: string) => string> = {
  AGENT_REQUIRED: () => 'Elige en «Agente que vende» a quién se le asigna la póliza.',
  UNKNOWN_AGENT: () => 'Ese agente no es de tu agencia; elige otro en «Agente que vende».',
  // The carrier answers a solicitud after its review (G&T): asking again follows the same one.
  ISSUANCE_PENDING: (m) =>
    `La aseguradora todavía revisa la solicitud${/request (\S+);/.exec(m) ? ` ${/request (\S+);/.exec(m)?.[1]}` : ''}. Nubi la emite en cuanto responda: la ves en Pólizas, «En revisión de la aseguradora».`,
};

/** Onboarding: the SIB credential a candidate already holds and its evidence. */
const SIB_ERRORS: Record<string, (message: string) => string> = {
  INVALID_SIB_CREDENTIAL: () =>
    'Escribe el número como aparece en la credencial, por ejemplo SIB-AG-40112.',
  SIB_EVIDENCE_REQUIRED: () =>
    'Adjunta primero la evidencia de la credencial (PDF o foto) para poder validarla.',
  SIB_CREDENTIAL_VALIDATED: () => 'La credencial ya se validó; no cambia.',
  SIB_CREDENTIAL_REVIEWED: () =>
    'La credencial ya se revisó. Adjunta una evidencia nueva para revisarla otra vez.',
  EVIDENCE_TYPE_NOT_ALLOWED: () => 'La evidencia debe ser un PDF o una foto (JPG, PNG o WebP).',
  EVIDENCE_TOO_LARGE: () => 'La evidencia pesa más de 5 MB.',
  EVIDENCE_EMPTY: () => 'El archivo está vacío.',
};

/** The psychometric evaluation of an onboarding, said from the administration's side. */
const PSYCHOMETRIC_ERRORS: Record<string, (message: string) => string> = {
  PSYCHOMETRIC_EVIDENCE_REQUIRED: () =>
    'Para registrarla como apta, primero sube el informe en PDF o envía la prueba en línea.',
  PSYCHOMETRIC_TEST_NOT_CONFIGURED: () =>
    'La agencia no tiene una prueba en línea: guarda su enlace en «Prueba psicométrica externa».',
  PSYCHOMETRIC_NEEDS_RESULT: () =>
    'La evaluación psicométrica se registra con su resultado, en su propio formulario.',
  REPORT_MUST_BE_PDF: () => 'El informe debe ser un archivo PDF.',
  REPORT_TOO_LARGE: () => 'El informe debe ser un PDF de hasta 5 MB.',
  INVALID_TEST_URL: () => 'El enlace de la prueba debe ser una dirección https:// válida.',
  INVALID_DATE: () => 'Revisa la fecha de aplicación: no puede ser una fecha futura.',
  APPLIED_BY_REQUIRED: () => 'Escribe quién aplicó la evaluación.',
  OBSERVATIONS_REQUIRED: () => 'Escribe las observaciones.',
  REASON_REQUIRED: () => 'Escribe el motivo.',
  CANDIDATE_HAS_NO_PHONE: () => 'La candidata no tiene teléfono para WhatsApp; envíalo por correo.',
  MESSAGE_NOT_SENT: () => 'No se pudo enviar el mensaje a la candidata. Intenta de nuevo.',
};

/** Following the recruiting (#319): reactivation, the SIB expiry and the certificate. */
const TRACKING_ERRORS: Record<string, (message: string) => string> = {
  AGENT_NOT_DEACTIVATED: () => 'Solo se reactiva a un agente dado de baja.',
  AGENT_ID_TAKEN: () =>
    'Su código de agente ya lo usa otro expediente activo o en proceso; no se puede reactivar con ese código.',
  CERTIFICATE_NOT_FOUND: () =>
    'No tiene constancia en Nubi: aprueba el examen final del programa asignado para emitirla.',
  SIB_NOT_REGISTERED: () => 'Registra primero la autorización SIB; luego su vencimiento.',
  INVALID_EXPIRY: () => 'Escribe la fecha de vencimiento como día, mes y año.',
};

/** Payment links: what stops one from reaching the policyholder. */
const PAYMENT_LINK_ERRORS: Record<string, (message: string) => string> = {
  NO_CONSENT: () =>
    'El contratante no ha aceptado mensajes por WhatsApp ni por correo. Regístralo en «Consentimiento de mensajes».',
  POLICYHOLDER_HAS_NO_PHONE: () => 'El contratante no tiene teléfono registrado para WhatsApp.',
  POLICYHOLDER_UNKNOWN: () => 'Esta póliza no tiene registrado a su contratante.',
  NO_PAYMENT_GATEWAY: () => 'La agencia no tiene configurada una pasarela de pagos.',
};

/** A renewal's follow-up («Renovaciones», ADR-0034), said from the user's side. */
const RENEWAL_ERRORS: Record<string, (message: string) => string> = {
  OFFER_REQUIRED: () =>
    'Consulta primero la oferta de la aseguradora: la propuesta le dice al contratante su prima.',
  NO_ADDRESS: () => 'La póliza no tiene ese teléfono o correo del contratante.',
  RENEWAL_CLOSED: () => 'Esta renovación ya se marcó como no renovada: ya no cambia.',
  ALREADY_RENEWED: () => 'Esta póliza ya se renovó: su gestión está cerrada.',
  POLICY_NOT_RENEWABLE: () =>
    'La póliza está cancelada o caducada: no hay renovación que gestionar.',
  NO_PERIOD_END: () =>
    'Nubi no conoce el fin de vigencia de esta póliza: no se puede gestionar su renovación.',
  REQUOTE_WITHOUT_QUOTE: () =>
    'Esta póliza se emitió sin una cotización guardada: cotízala desde «Cotizar» con los datos del cliente.',
  CONTACT_NOTE_REQUIRED: () => 'Escribe qué se habló con el contratante.',
  CONTACT_NOTE_TOO_LONG: () => 'La nota admite hasta 500 caracteres.',
  LOST_NOTE_REQUIRED: () => 'Con «Otro motivo», escribe por qué no se renueva.',
  LOST_NOTE_TOO_LONG: () => 'La nota admite hasta 500 caracteres.',
  ASSIGNEE_REQUIRED: () => 'Elige quién gestiona la renovación.',
  RENEWAL_BY_WORK_ORDER: () =>
    'La aseguradora renueva esta póliza por orden de trabajo: pídela desde «Pólizas y recibos» › Renovar.',
};

/** Claims («Siniestros», ADR-0038), said from the user's side. */
const CLAIM_ERRORS: Record<string, (message: string) => string> = {
  OCCURRED_IN_FUTURE: () => 'La fecha de ocurrencia no puede ser después de hoy.',
  CAUSE_NOT_COVERED: () => 'La aseguradora no recibe ese tipo de siniestro para este ramo.',
  DEDUCTIBLE_ABOVE_APPROVED: () => 'El deducible debe ser menor que el monto aprobado.',
  PAYMENT_EXCEEDS: () => 'El pago es mayor que lo que queda por pagar del siniestro.',
  DUPLICATE_PAYMENT: () => 'Ese pago ya está registrado (misma referencia).',
  INVALID_TRANSITION: () => 'El siniestro ya no admite esa decisión en el estado en que está.',
  CARRIER_DECIDES: () =>
    'Este siniestro lo sigue la integración de la aseguradora: consúltalo con «Consultar a la aseguradora».',
  CARRIER_NOT_INTEGRATED: () =>
    'Este siniestro va por orden de trabajo: registra a mano lo que responda la aseguradora.',
  CARRIER_REF_SET: () => 'La aseguradora ya le dio su número a este siniestro.',
  PAID_IN_FUTURE: () => 'La fecha del pago no puede ser después de hoy.',
};

/** The administration of Nubi's agencies, and an agency that cannot enter (ADR-0024). */
const PLATFORM_ERRORS: Record<string, (message: string) => string> = {
  AGENCY_SUSPENDED: () =>
    'Tu agencia está suspendida en Nubi. Tus datos se conservan; escribe a la administración de Nubi para reactivarla.',
  AGENCY_NOT_REGISTERED: () =>
    'Tu agencia no está registrada en Nubi. Escribe a la administración de Nubi.',
  AGENCY_STATUS_UNAVAILABLE: () =>
    'No pudimos comprobar el estado de tu agencia. Intenta de nuevo en un momento.',
  AGENCY_EXISTS: () => 'Ya hay una agencia con ese código. Elige otro.',
  AGENCY_ID_RESERVED: () => 'Ese código no puede ser de una agencia. Elige otro.',
  AGENCY_NOT_FOUND: () => 'Esa agencia no está en la plataforma. Actualiza la pantalla.',
  // The platform's carriers (#283, ADR-0027).
  CARRIER_NOT_OFFERED: () =>
    'Esa aseguradora ya no cotiza ni emite pólizas nuevas en Nubi. Lo ya emitido con ella sigue igual; cotiza con las demás.',
  PLATFORM_CARRIERS_UNAVAILABLE: () =>
    'No pudimos leer el estado de las aseguradoras. Intenta de nuevo en un momento.',
  APPOINTMENT_NOT_FOUND: () =>
    'Esa agencia no trabaja con la aseguradora. Configúrala primero en «Agencias» › «Aseguradoras».',
  IDENTITY_UNAVAILABLE: () =>
    'Stytch no respondió: la invitación sigue pendiente. Intenta de nuevo en un momento.',
  ORGANIZATION_CONFLICT: () =>
    'Stytch ya tiene una organización con ese código que no es de esta agencia. Revisa el código con soporte.',
  // The people of Nubi's own organization (#274).
  LAST_ADMIN: () =>
    'Nubi necesita al menos una persona administradora activa. Invita a otra y espera a que entre antes de cambiar este rol o quitar este acceso.',
  CANNOT_REMOVE_SELF: () =>
    'No puedes quitarte tu propio acceso. Pide a otra persona administradora que lo haga.',
  ADMIN_NOT_FOUND: () => 'Esa persona ya no está en Nubi. Actualiza la pantalla.',
  NOT_NUBI_ADMINISTRATION: () =>
    'Ya no administras Nubi: tu rol cambió o te quitaron el acceso. Vuelve a entrar.',
  // The platform's notice to every agency (#284).
  NOTICE_INVALID: () =>
    'El aviso es texto plano de 1 a 280 caracteres, sin < ni >, con tono información o importante.',
  NOTICE_ENDS_BEFORE_TODAY: () => 'La fecha «Vigente hasta» no puede ser antes de hoy.',
  NOTICE_CONFLICT: () =>
    'Otra persona acaba de publicar un aviso. Actualiza la pantalla y revisa el aviso antes de cambiarlo.',
  // Each agency's module switches (#285, ADR-0029).
  MODULE_OFF: () =>
    'Este módulo está apagado para tu agencia. Si lo necesitas, pide a la administración de Nubi que lo encienda.',
  AGENCY_MODULES_UNAVAILABLE: () =>
    'No pudimos comprobar los módulos de tu agencia. Intenta de nuevo en un momento.',
  MODULE_NOT_SWITCHABLE: () =>
    'Ese módulo no se puede apagar: solo los módulos periféricos se encienden y apagan por agencia.',
  NUBI_ORGANIZATION_INVALID: () =>
    'La organización de Nubi en Stytch no está configurada como la de Nubi. Revísala con soporte.',
};

/** The customer record (#312), said from the person's side. */
const CUSTOMER_ERRORS: Record<string, (message: string) => string> = {
  // The API names the customer and how many policies it has in force.
  PARTY_HAS_POLICIES_IN_FORCE: (m) => m,
  PARTY_ALREADY_CLOSED: () => 'Este cliente ya está de baja.',
  CLOSURE_NEEDS_NOTE: () => 'Escribe el motivo de la baja.',
  PEP_NEEDS_POSITION: () => 'Escribe el cargo público de la persona expuesta políticamente.',
  ALREADY_RELATED: () => 'Esa relación ya está registrada.',
  RELATIONSHIP_WITH_ITSELF: () => 'Un cliente no se relaciona consigo mismo.',
  RELATIONSHIP_KIND_MISMATCH: () =>
    'Esa relación no aplica: las familiares son entre personas y las de empresa, con una empresa.',
  NOTE_LOCKED: () =>
    'Esa nota ya no se puede corregir: pasaron más de 24 horas. Agrega una nota nueva.',
  NOTE_NOT_YOURS: () => 'Solo quien escribió la nota la corrige. Agrega una nota nueva.',
  NOTE_EMPTY: () => 'Escribe la nota.',
  INVALID_PHONE: () => 'El teléfono lleva 8 dígitos, con o sin +502.',
  INVALID_EMAIL: () => 'El correo no es válido.',
  INVALID_NATIONAL_ID: () => 'El DPI (CUI) lleva 13 dígitos.',
  INVALID_TAX_ID: () => 'Escribe el NIT con guion antes del último dígito, por ejemplo 1234567-8.',
  DOCUMENT_TYPE_NOT_ALLOWED: () => 'Solo se aceptan PDF e imágenes JPG, PNG o WebP.',
  DOCUMENT_TOO_LARGE: () => 'El archivo pesa más de 5 MB.',
  DOCUMENT_EMPTY: () => 'El archivo está vacío.',
};

/**
 * What the person reads first; the API's code and message go underneath, for support. When
 * Nubi says the code in its own words (`translated`), the code is folded away (#235, F6):
 * only a sentence that says little (by HTTP status) keeps it in sight.
 */
/** The agency's tasks (#317), said from the person's side. */
const TASK_ERRORS: Record<string, (message: string) => string> = {
  TASK_TITLE_REQUIRED: () => 'Escribe qué hay que hacer.',
  TASK_TITLE_TOO_LONG: () => 'El título de la tarea admite hasta 200 caracteres.',
  TASK_OUTCOME_REQUIRED: () => 'Escribe el resultado de la tarea.',
  TASK_REASON_REQUIRED: () => 'Escribe por qué se anula la tarea.',
  TASK_OUTCOME_TOO_LONG: () => 'El resultado o el motivo admite hasta 1,000 caracteres.',
  TASK_CLOSED: () => 'Esta tarea ya está cerrada: no se puede cambiar.',
  TASK_SAME_ASSIGNEE: () => 'La tarea ya es de esa persona.',
  TASK_ASSIGNEE_UNKNOWN: () => 'Esa persona no está en el equipo de la agencia.',
  TASK_DUE_INVALID: () => 'Elige la fecha y la hora de vencimiento.',
  TASK_NOT_FOUND: () => 'Esa tarea ya no está disponible.',
  RECEIPT_NOT_FOUND: () => 'Ese recibo no existe en la póliza.',
};

export function explain(e: unknown): { human: string; detail?: string; translated?: true } {
  if (e instanceof InputError) return { human: e.message };
  if (!(e instanceof ApiError))
    return { human: 'No hubo respuesta del servidor. Revisa tu conexión e intenta de nuevo.' };
  const detail = `${e.code}: ${e.message}`;
  const rules =
    RULE_ERRORS[e.code] ??
    REMINDER_ERRORS[e.code] ??
    ISSUE_ERRORS[e.code] ??
    ADVANCE_ERRORS[e.code] ??
    STATEMENT_ERRORS[e.code] ??
    REPORT_ERRORS[e.code] ??
    INVOICE_ERRORS[e.code] ??
    PSYCHOMETRIC_ERRORS[e.code] ??
    TRACKING_ERRORS[e.code] ??
    FORM_ERRORS[e.code] ??
    SIB_ERRORS[e.code] ??
    RENEWAL_ERRORS[e.code] ??
    CLAIM_ERRORS[e.code] ??
    PAYMENT_LINK_ERRORS[e.code] ??
    PLATFORM_ERRORS[e.code] ??
    CUSTOMER_ERRORS[e.code] ??
    TASK_ERRORS[e.code];
  if (rules) return { human: rules(e.message), detail, translated: true };
  // Carriers explain their refusals in their own words (e.g. "El mes 2026-09 aún no cierra").
  if (e.code.startsWith('CARRIER_'))
    return {
      human: `La aseguradora no aceptó la solicitud: ${
        e.message
          .split(/→ \d{3} ?/)
          .pop()
          ?.trim() || e.message
      }`,
      detail,
      translated: true,
    };
  const human =
    e.status === 401
      ? 'Tu sesión terminó. Vuelve a entrar.'
      : e.status === 403
        ? 'Tu rol no permite hacer esto.'
        : e.status === 404
          ? 'No encontramos ese registro en tu agencia.'
          : e.status === 409
            ? 'Alguien más lo cambió antes. Actualiza la pantalla e intenta de nuevo.'
            : e.status === 422
              ? 'La solicitud no cumple las reglas de la aseguradora o de Nubi.'
              : e.status === 502 || e.status === 503
                ? 'Un servicio no respondió a tiempo. Intenta de nuevo en un momento.'
                : 'Algo falló en Nubi. Intenta de nuevo.';
  return { human, detail };
}
